import { cloneDeep, isEqual, omit, pick } from 'lodash-es';
import { Storage_Locality } from '@schema';
import type {
  ComputeProvisioningStoragePolicies,
  DiskType,
  StoragePolicyIntent,
  StoragePolicyRedundancy as SchemaStoragePolicyRedundancy,
  StorageProvisioningNodeIntent
} from '@schema';
import type { ComputeClusterFormModel } from '@/common/store/pinia/computeProvisioning';
import {
  DEFAULT_POLICY_NAME,
  REDUNDANCY_ENCODING_TYPE,
  REDUNDANCY_MAP,
  REDUNDANCY_REPLICA_TYPE
} from '@/common/constants/policy.constants';
import {
  getLocalTierOptions,
  getPolicyFailureDomainCount,
  getPolicyMedia,
  MEDIUM_ORDER
} from '@/hci/pages/infrastructure/nodes/createComputeClusterWizard/computeClusterStorageTiers';
import type { GetInventoryDisk } from '@/hci/pages/infrastructure/nodes/createComputeClusterWizard/computeClusterStorageTiers';
import i18n from '@/common/i18n';

// Redundancy with n optional, which replication leaves out.
export type StoragePolicyRedundancy = Omit<SchemaStoragePolicyRedundancy, 'n'> & { n?: number };

// Hands a redundancy to a storage policy intent, whose schema type requires n.
export const toSchemaRedundancy = (redundancy?: StoragePolicyRedundancy): SchemaStoragePolicyRedundancy | undefined => (
  redundancy as SchemaStoragePolicyRedundancy | undefined
);

// Drops a non-positive n from a replication redundancy.
export const withoutReplicaZeroN = (redundancy?: StoragePolicyRedundancy): SchemaStoragePolicyRedundancy | undefined => (
  toSchemaRedundancy(redundancy?.type === REDUNDANCY_REPLICA_TYPE && (redundancy.n ?? 0) <= 0
    ? omit(redundancy, 'n')
    : redundancy)
);

export type StoragePolicyRedundancyOption = {
  key: string;
  label: string;
  value: StoragePolicyRedundancy;
};

export type StoragePolicyRedundancyOptionGroup = {
  key: StoragePolicyRedundancy['type'];
  label: string;
  options: StoragePolicyRedundancyOption[];
};

type RedundancyVariant = {
  key: string;
  need: number;
  redundancy: { type: StoragePolicyRedundancy['type']; m: number; n?: number };
  overhead: number;
};

export const NO_REDUNDANCY_KEY = 'none';

export const DEFAULT_REDUNDANCY_KEY = 'raid1:2';

// Replication leaves its minimum replica count to the backend.
export const NO_REDUNDANCY: StoragePolicyRedundancy = { type: REDUNDANCY_REPLICA_TYPE, m: 1 };

// A single data copy without parity keeps no redundancy; replication ignores the parity count.
export const isNoRedundancy = (redundancy?: StoragePolicyRedundancy | null): boolean => !redundancy
  || (redundancy.m < 2 && (redundancy.type !== REDUNDANCY_ENCODING_TYPE || !redundancy.n));

// Serializes a redundancy value into a string key usable as a select option value.
export const redundancyOptionKey = (redundancy?: StoragePolicyRedundancy | null): string => {
  if (isNoRedundancy(redundancy)) {
    return NO_REDUNDANCY_KEY;
  }
  const { type, m, n } = redundancy as StoragePolicyRedundancy;
  return type === REDUNDANCY_ENCODING_TYPE ? `${type}:${m}+${n}` : `${type}:${m}`;
};

// Parses a select option key back into a redundancy value.
export const parseRedundancyOptionKey = (key: string): StoragePolicyRedundancy => {
  if (key === NO_REDUNDANCY_KEY) {
    return { ...NO_REDUNDANCY };
  }
  const [type, scheme] = key.split(':');
  const [m, n] = scheme.split('+');
  // Only erasure coding keys carry n.
  return type === REDUNDANCY_ENCODING_TYPE
    ? { type: type as StoragePolicyRedundancy['type'], m: Number(m), n: Number(n) }
    : { type: type as StoragePolicyRedundancy['type'], m: Number(m) };
};

export const formatRedundancyOverhead = (overhead: number): string => (overhead
  ? i18n.t('{redundancyOverhead}% overhead', { redundancyOverhead: overhead })
  : i18n.t('No redundancy'));

export const formatRedundancyOptionLabel = (redundancy: StoragePolicyRedundancy, overhead: number): string => (
  redundancy.type === REDUNDANCY_ENCODING_TYPE
    ? i18n.t('Encoding {redundancyChunks}+{redundancyParityChunks} ({overhead})', {
      redundancyChunks: redundancy.m,
      redundancyParityChunks: redundancy.n,
      overhead: formatRedundancyOverhead(overhead)
    })
    : i18n.t('{redundancyChunks} replicas ({overhead})', {
      redundancyChunks: redundancy.m,
      overhead: formatRedundancyOverhead(overhead)
    })
);

const buildGroup = (
  type: StoragePolicyRedundancy['type'],
  label: string,
  failureDomainCount: number
): StoragePolicyRedundancyOptionGroup => ({
  key: type,
  label,
  options: (Object.values(REDUNDANCY_MAP[type]) as RedundancyVariant[])
    .filter(variant => variant.need > 1 && variant.need <= failureDomainCount)
    .map((variant) => {
      // Only erasure coding option values carry n.
      const value: StoragePolicyRedundancy = variant.redundancy.type === REDUNDANCY_ENCODING_TYPE
        ? { type: variant.redundancy.type, m: variant.redundancy.m, n: variant.redundancy.n || 0 }
        : { type: variant.redundancy.type, m: variant.redundancy.m };
      return {
        key: redundancyOptionKey(value),
        label: formatRedundancyOptionLabel(value, variant.overhead),
        value
      };
    })
});

// The replication and erasure coding schemes the given number of failure domains can hold.
export const getStoragePolicyRedundancyOptionGroups = (
  failureDomainCount: number
): StoragePolicyRedundancyOptionGroup[] => [
  buildGroup(REDUNDANCY_REPLICA_TYPE, i18n.t('Replication'), failureDomainCount),
  buildGroup(REDUNDANCY_ENCODING_TYPE, i18n.t('Erasure coding'), failureDomainCount)
].filter(group => group.options.length > 0);

// Keeps a redundancy the failure domains can hold, otherwise lowers it to replica 2 when that fits, else to no redundancy.
export const fitStoragePolicyRedundancy = (
  redundancy: StoragePolicyRedundancy | undefined,
  failureDomainCount: number
): StoragePolicyRedundancy | undefined => {
  const keys = [
    NO_REDUNDANCY_KEY,
    ...getStoragePolicyRedundancyOptionGroups(failureDomainCount)
      .flatMap(group => group.options.map(option => option.key))
  ];
  if (keys.includes(redundancyOptionKey(redundancy))) {
    return redundancy;
  }
  return parseRedundancyOptionKey(keys.includes(DEFAULT_REDUNDANCY_KEY) ? DEFAULT_REDUNDANCY_KEY : NO_REDUNDANCY_KEY);
};

// Whether the failure domains can hold the replica-2 default.
export const holdsDefaultRedundancy = (failureDomainCount: number): boolean => redundancyOptionKey(
  fitStoragePolicyRedundancy(parseRedundancyOptionKey(DEFAULT_REDUNDANCY_KEY), failureDomainCount)
) === DEFAULT_REDUNDANCY_KEY;

// The local tier numbers of the policy's disk types on the nodes it selects, lowest first.
const sameMediaLocalTierNumbers = (
  policy: StoragePolicyIntent,
  nodeIds: string[],
  storageNodes: StorageProvisioningNodeIntent[] | null | undefined,
  getInventoryDisk: GetInventoryDisk
): number[] => {
  const media = getPolicyMedia(policy, storageNodes, getInventoryDisk);
  const tiers = getLocalTierOptions(storageNodes, getInventoryDisk)
    .filter(tier => nodeIds.includes(tier.nodeId) && isEqual(tier.media, media));
  return [...new Set(tiers.map(tier => tier.tierNum))].sort((a, b) => a - b);
};

// Moves a local policy whose tiers cannot hold the replica-2 default onto the lowest tier of the same disk types that can.
export const fitStoragePolicyTiers = (
  policy: StoragePolicyIntent,
  storageNodes: StorageProvisioningNodeIntent[] | null | undefined,
  getInventoryDisk: GetInventoryDisk
): StoragePolicyIntent => {
  const tierSelector = policy.tier_selector;
  if (tierSelector?.locality !== Storage_Locality.LOCAL) {
    return policy;
  }
  const current = getPolicyFailureDomainCount(policy, storageNodes, getInventoryDisk);
  if (current === null || holdsDefaultRedundancy(current)) {
    return policy;
  }
  const withTier = (tierNum: number): StoragePolicyIntent => ({
    ...policy,
    tier_selector: { ...tierSelector, tier_numbers: [tierNum] }
  });
  const tierNum = sameMediaLocalTierNumbers(policy, tierSelector.node_ids, storageNodes, getInventoryDisk)
    .find(candidate => holdsDefaultRedundancy(
      getPolicyFailureDomainCount(withTier(candidate), storageNodes, getInventoryDisk) || 0
    ));
  return tierNum === undefined ? policy : withTier(tierNum);
};

type RequestedStoragePolicyKey = keyof ComputeClusterFormModel['requestedStoragePolicies'][string];

// The redundancy and failure domain, or the given fields, every tier-backed policy asks for, keyed by its client ref.
export const getRequestedStoragePolicies = (
  policies: StoragePolicyIntent[],
  keys: RequestedStoragePolicyKey[] = ['redundancy', 'failure_domain']
): ComputeClusterFormModel['requestedStoragePolicies'] => Object.fromEntries(policies
  .filter(policy => !!policy.tier_selector)
  .map(policy => [policy.client_ref, cloneDeep(pick(policy, keys))]));

// Ranks a set of disk types by its slowest one.
const mediaRank = (media: DiskType[]): number => {
  const ranks = media.map(medium => MEDIUM_ORDER.indexOf(medium)).filter(rank => rank !== -1);
  return ranks.length ? Math.max(...ranks) : MEDIUM_ORDER.length;
};

// Picks the tier-backed policy of the fastest disk type that keeps redundancy, else of the fastest disk type.
export const pickDefaultStoragePolicyRef = (
  policies: StoragePolicyIntent[],
  currentRef: string,
  mediaOf: (policy: StoragePolicyIntent) => DiskType[]
): string => {
  const mediumRank = (policy: StoragePolicyIntent): number => mediaRank(mediaOf(policy));
  const candidates = policies.filter(policy => !!policy.tier_selector);
  const redundant = candidates.filter(policy => !isNoRedundancy(policy.redundancy));
  const pool = redundant.length ? redundant : candidates;
  if (!pool.length) {
    return currentRef;
  }
  const fastestRank = Math.min(...pool.map(mediumRank));
  const fastest = pool.filter(policy => mediumRank(policy) === fastestRank);
  return (fastest.find(policy => policy.client_ref === currentRef) || fastest[0]).client_ref;
};

// The name the storage plan gives a policy it did not make the default, read back from its client ref:
// `policy-<medium>-shared-<tier>` is `shared-<medium>-<tier>` and `policy-<label>` is `local-<label>`.
export const planStoragePolicyName = (clientRef: string): string | undefined => {
  const shared = /^policy-(.+)-shared-(\d+)$/.exec(clientRef);
  if (shared) {
    return `shared-${shared[1]}-${shared[2]}`;
  }
  const local = /^policy-(.+)$/.exec(clientRef);
  return local ? `local-${local[1]}` : undefined;
};

// The backend accepts the initial default only when that policy is named `default`
// (DESIGN-compute-provisioning-wizard-api §5.4, error `initial_default_policy_invalid`),
// while the wizard moves the default to the fastest disk type that keeps redundancy (PRD AC-004).
// So the name moves with the default: the policy taking it is renamed to `default`, and the policy
// giving it up gets its plan name back, or the name the new default had when that one is unknown or taken.
// Returns the new name of every policy to rename, keyed by its client ref.
export const getDefaultStoragePolicyRenames = (
  policies: ComputeProvisioningStoragePolicies
): Record<string, string> => {
  const nextDefault = policies.items.find(policy => policy.client_ref === policies.initial_default_ref);
  if (!nextDefault?.tier_selector || nextDefault.name === DEFAULT_POLICY_NAME) {
    return {};
  }
  const names = new Set(policies.items.map(policy => policy.name));
  const renames: Record<string, string> = { [nextDefault.client_ref]: DEFAULT_POLICY_NAME };
  policies.items
    .filter(policy => policy.name === DEFAULT_POLICY_NAME)
    .forEach((previousDefault) => {
      const planName = planStoragePolicyName(previousDefault.client_ref);
      renames[previousDefault.client_ref] = planName && !names.has(planName) ? planName : nextDefault.name;
    });
  return renames;
};

// Renames the policies of a policy set so the default one is named `default`.
export const nameDefaultStoragePolicy = (policies: ComputeProvisioningStoragePolicies): void => {
  Object.entries(getDefaultStoragePolicyRenames(policies)).forEach(([clientRef, name]) => {
    const index = policies.items.findIndex(policy => policy.client_ref === clientRef);
    policies.items.splice(index, 1, { ...policies.items[index], name });
  });
};
